import React, { useState, useEffect } from 'react';
import { FaSearch, FaHome, FaBed, FaPlus, FaEdit, FaTrash, FaFilter, FaTimes, FaBuilding, FaExclamationTriangle } from 'react-icons/fa';
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "../../css/HostelRooms.css";

function HostelRooms() {
    const [hostels, setHostels] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [filteredRooms, setFilteredRooms] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterOption, setFilterOption] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [activeTab, setActiveTab] = useState('hostels');

    // Modals for hostels
    const [showAddHostelModal, setShowAddHostelModal] = useState(false);
    const [showEditHostelModal, setShowEditHostelModal] = useState(false);
    const [showDeleteHostelModal, setShowDeleteHostelModal] = useState(false);
    const [currentHostel, setCurrentHostel] = useState(null);
    const [hostelFormData, setHostelFormData] = useState({
        name: '',
        type: 'boys', // Default to boys
        capacity: '',
        location: '',
        warden: '',
        contactNumber: '',
        facilities: '',
        description: ''
    });

    // Modals for rooms
    const [showAddRoomModal, setShowAddRoomModal] = useState(false);
    const [showEditRoomModal, setShowEditRoomModal] = useState(false);
    const [showDeleteRoomModal, setShowDeleteRoomModal] = useState(false);
    const [currentRoom, setCurrentRoom] = useState(null);
    const [roomFormData, setRoomFormData] = useState({
        roomNumber: '',
        hostelId: '',
        capacity: '',
        floor: '',
        type: 'standard', // Default to standard
        status: 'available', // Default to available
        occupants: 0,
        rentPerMonth: '',
        amenities: ''
    });

    useEffect(() => {
        fetchHostels();
        fetchRooms();
    }, []);

    // Fetch hostels from backend
    const fetchHostels = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            // Replace with your actual API endpoint
            const response = await fetch("https://schoolpro-api.uniums.com/api/hostels", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch hostels");
            }

            const data = await response.json();
            setHostels(data);
        } catch (error) {
            console.error("Error fetching hostels:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch rooms from backend
    const fetchRooms = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            // Replace with your actual API endpoint
            const response = await fetch("https://schoolpro-api.uniums.com/api/hostels/rooms", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch rooms");
            }

            const data = await response.json();
            setRooms(data);
            setFilteredRooms(data);
        } catch (error) {
            console.error("Error fetching rooms:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle hostel form input changes
    const handleHostelInputChange = (e) => {
        const { name, value } = e.target;
        setHostelFormData({
            ...hostelFormData,
            [name]: value
        });
    };

    // Handle room form input changes
    const handleRoomInputChange = (e) => {
        const { name, value } = e.target;
        setRoomFormData({
            ...roomFormData,
            [name]: value
        });
    };

    // Handle adding new hostel
    const handleAddHostelSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            // Replace with your actual API endpoint
            const response = await fetch("https://schoolpro-api.uniums.com/api/hostels/save", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(hostelFormData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to add hostel");
            }

            const data = await response.json();

            // Update hostels state with new hostel
            setHostels([...hostels, data.hostel]);

            toast.success("Hostel added successfully!");
            setShowAddHostelModal(false);

            // Reset form data
            setHostelFormData({
                name: '',
                type: 'boys',
                capacity: '',
                location: '',
                warden: '',
                contactNumber: '',
                facilities: '',
                description: ''
            });
        } catch (error) {
            console.error("Error adding hostel:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle updating hostel
    const handleUpdateHostelSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            // Replace with your actual API endpoint
            const response = await fetch(`https://schoolpro-api.uniums.com/api/hostels/edit/${currentHostel._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(hostelFormData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update hostel");
            }

            const data = await response.json();

            // Update hostels array with updated data
            const updatedHostels = hostels.map(hostel =>
                hostel._id === currentHostel._id ? data.hostel : hostel
            );

            setHostels(updatedHostels);

            toast.success("Hostel updated successfully!");
            setShowEditHostelModal(false);
        } catch (error) {
            console.error("Error updating hostel:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle deleting hostel
    const handleDeleteHostelConfirm = async () => {
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            // Replace with your actual API endpoint
            const response = await fetch(`https://schoolpro-api.uniums.com/api/hostels/delete/${currentHostel._id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete hostel");
            }

            // Remove the deleted hostel from state
            const updatedHostels = hostels.filter(hostel => hostel._id !== currentHostel._id);
            setHostels(updatedHostels);

            toast.success("Hostel deleted successfully!");
            setShowDeleteHostelModal(false);
        } catch (error) {
            console.error("Error deleting hostel:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle adding new room
    const handleAddRoomSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            // Replace with your actual API endpoint
            const response = await fetch("https://schoolpro-api.uniums.com/api/hostels/rooms-save", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(roomFormData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to add room");
            }

            const data = await response.json();

            // Update rooms state with new room
            setRooms([...rooms, data.room]);
            setFilteredRooms([...filteredRooms, data.room]);

            toast.success("Room added successfully!");
            setShowAddRoomModal(false);

            // Reset form data
            setRoomFormData({
                roomNumber: '',
                hostelId: '',
                capacity: '',
                floor: '',
                type: 'standard',
                status: 'available',
                occupants: 0,
                rentPerMonth: '',
                amenities: ''
            });
        } catch (error) {
            console.error("Error adding room:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle updating room
    const handleUpdateRoomSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            // Replace with your actual API endpoint
            const response = await fetch(`https://schoolpro-api.uniums.com/api/hostels/rooms-edit/${currentRoom._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(roomFormData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update room");
            }

            const data = await response.json();

            // Update rooms array with updated data
            const updatedRooms = rooms.map(room =>
                room._id === currentRoom._id ? data.room : room
            );

            setRooms(updatedRooms);

            // Apply current filters to updated data
            handleFilterRooms(updatedRooms);

            toast.success("Room updated successfully!");
            setShowEditRoomModal(false);
        } catch (error) {
            console.error("Error updating room:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle deleting room
    const handleDeleteRoomConfirm = async () => {
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            // Replace with your actual API endpoint
            const response = await fetch(`https://schoolpro-api.uniums.com/api/hostels/rooms-delete/${currentRoom._id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete room");
            }

            // Remove the deleted room from state
            const updatedRooms = rooms.filter(room => room._id !== currentRoom._id);
            setRooms(updatedRooms);

            // Apply current filters to updated data
            handleFilterRooms(updatedRooms);

            toast.success("Room deleted successfully!");
            setShowDeleteRoomModal(false);
        } catch (error) {
            console.error("Error deleting room:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle opening the edit hostel modal
    const handleEditHostelClick = (hostel) => {
        setCurrentHostel(hostel);
        setHostelFormData({ ...hostel });
        setShowEditHostelModal(true);
    };

    // Handle opening the delete hostel modal
    const handleDeleteHostelClick = (hostel) => {
        setCurrentHostel(hostel);
        setShowDeleteHostelModal(true);
    };

    // Handle opening the edit room modal
    const handleEditRoomClick = (room) => {
        setCurrentRoom(room);
        setRoomFormData({ ...room });
        setShowEditRoomModal(true);
    };

    // Handle opening the delete room modal
    const handleDeleteRoomClick = (room) => {
        setCurrentRoom(room);
        setShowDeleteRoomModal(true);
    };

    // Handle search
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        handleFilterRooms(rooms, e.target.value, filterOption);
    };

    // Handle filter change
    const handleFilterChange = (e) => {
        const newFilterOption = e.target.value;
        setFilterOption(newFilterOption);
        handleFilterRooms(rooms, searchTerm, newFilterOption);
    };

    // Filter rooms based on search term and filter option
    const handleFilterRooms = (roomsData, search = searchTerm, filter = filterOption) => {
        let results = roomsData;

        // Apply search
        if (search) {
            results = results.filter(room =>
                (room.roomNumber?.toString() || "").includes(search) ||
                (room.floor?.toString() || "").includes(search) ||
                (room.type?.toLowerCase() || "").includes(search.toLowerCase()) ||
                (room.status?.toLowerCase() || "").includes(search.toLowerCase())
            );
        }

        // Apply filters
        if (filter !== 'all') {
            if (filter === 'available' || filter === 'occupied' || filter === 'maintenance') {
                results = results.filter(room =>
                    room.status?.toLowerCase() === filter
                );
            } else {
                // Filter by hostel
                results = results.filter(room =>
                    room.hostelId === filter
                );
            }
        }

        setFilteredRooms(results);
    };

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    // Get hostel name by ID
    const getHostelNameById = (hostelId) => {
        const hostel = hostels.find(h => h._id === hostelId);
        return hostel ? hostel.name : 'Unknown Hostel';
    };

    return (
        <div className="hostelrooms-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            {isLoading && <Loader />}

            <div className="hostelrooms-header">
                <div className="hostelrooms-title-container">
                    <FaBuilding className="hostelrooms-icon" />
                    <h1 className="hostelrooms-title">Hostel Management</h1>
                </div>

                <div className="hostelrooms-tabs">
                    <button
                        className={`hostelrooms-tab ${activeTab === 'hostels' ? 'active' : ''}`}
                        onClick={() => setActiveTab('hostels')}
                    >
                        <FaHome /> Hostels
                    </button>
                    <button
                        className={`hostelrooms-tab ${activeTab === 'rooms' ? 'active' : ''}`}
                        onClick={() => setActiveTab('rooms')}
                    >
                        <FaBed /> Rooms
                    </button>
                </div>

                {activeTab === 'hostels' ? (
                    <div className="hostelrooms-controls">
                        <button
                            className="hostelrooms-add-btn"
                            onClick={() => setShowAddHostelModal(true)}
                        >
                            <FaPlus /> Add New Hostel
                        </button>
                    </div>
                ) : (
                    <div className="hostelrooms-controls">
                        <div className="hostelrooms-search-container">
                            <FaSearch className="hostelrooms-search-icon" />
                            <input
                                type="text"
                                className="hostelrooms-search-input"
                                placeholder="Search by room number, floor, type..."
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                        </div>

                        <button className="hostelrooms-filter-toggle-btn" onClick={toggleFilters}>
                            <FaFilter /> Filters
                        </button>

                        <button
                            className="hostelrooms-add-btn"
                            onClick={() => setShowAddRoomModal(true)}
                        >
                            <FaPlus /> Add New Room
                        </button>
                    </div>
                )}

                {showFilters && activeTab === 'rooms' && (
                    <div className="hostelrooms-filter-options">
                        <select
                            value={filterOption}
                            onChange={handleFilterChange}
                            className="hostelrooms-filter-select"
                        >
                            <option value="all">All Rooms</option>
                            <option value="available">Available Rooms</option>
                            <option value="occupied">Occupied Rooms</option>
                            <option value="maintenance">Under Maintenance</option>
                            {hostels.length > 0 && (
                                <optgroup label="Hostels">
                                    {hostels.map(hostel => (
                                        <option key={hostel._id} value={hostel._id}>{hostel.name}</option>
                                    ))}
                                </optgroup>
                            )}
                        </select>
                    </div>
                )}

                {activeTab === 'rooms' && (
                    <div className="hostelrooms-stats">
                        <div className="hostelrooms-stat-box">
                            <span className="hostelrooms-stat-number">{filteredRooms.length}</span>
                            <span className="hostelrooms-stat-label">Total Rooms</span>
                        </div>
                        <div className="hostelrooms-stat-box available">
                            <span className="hostelrooms-stat-number">
                                {filteredRooms.filter(room => room.status === 'available').length}
                            </span>
                            <span className="hostelrooms-stat-label">Available</span>
                        </div>
                        <div className="hostelrooms-stat-box occupied">
                            <span className="hostelrooms-stat-number">
                                {filteredRooms.filter(room => room.status === 'occupied').length}
                            </span>
                            <span className="hostelrooms-stat-label">Occupied</span>
                        </div>
                        <div className="hostelrooms-stat-box maintenance">
                            <span className="hostelrooms-stat-number">
                                {filteredRooms.filter(room => room.status === 'maintenance').length}
                            </span>
                            <span className="hostelrooms-stat-label">Maintenance</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Hostels Tab Content */}
            {activeTab === 'hostels' && (
                <>
                    {hostels.length === 0 && !isLoading ? (
                        <div className="hostelrooms-empty">
                            <div className="hostelrooms-empty-icon">
                                <FaHome size={48} color="#a0aec0" />
                            </div>
                            <p className="hostelrooms-empty-text">No hostels found. Please add a hostel.</p>
                            <button
                                className="hostelrooms-add-btn"
                                onClick={() => setShowAddHostelModal(true)}
                            >
                                <FaPlus /> Add New Hostel
                            </button>
                        </div>
                    ) : (
                        <div className="hostelrooms-grid">
                            {hostels.map((hostel, index) => (
                                <div key={index} className={`hostelrooms-hostel-card ${hostel.type}`}>
                                    <div className="hostelrooms-hostel-header">
                                        <h2 className="hostelrooms-hostel-name">{hostel.name}</h2>
                                        <span className="hostelrooms-hostel-type-badge">
                                            {hostel.type.charAt(0).toUpperCase() + hostel.type.slice(1)}
                                        </span>
                                    </div>

                                    <div className="hostelrooms-hostel-body">
                                        <div className="hostelrooms-hostel-info-section">
                                            <div className="hostelrooms-hostel-info-group">
                                                <span className="hostelrooms-hostel-info-label">Capacity:</span>
                                                <span className="hostelrooms-hostel-info-value">{hostel.capacity} Students</span>
                                            </div>
                                            <div className="hostelrooms-hostel-info-group">
                                                <span className="hostelrooms-hostel-info-label">Location:</span>
                                                <span className="hostelrooms-hostel-info-value">{hostel.location || 'N/A'}</span>
                                            </div>
                                            <div className="hostelrooms-hostel-info-group">
                                                <span className="hostelrooms-hostel-info-label">Warden:</span>
                                                <span className="hostelrooms-hostel-info-value">{hostel.warden || 'N/A'}</span>
                                            </div>
                                            <div className="hostelrooms-hostel-info-group">
                                                <span className="hostelrooms-hostel-info-label">Contact:</span>
                                                <span className="hostelrooms-hostel-info-value">{hostel.contactNumber || 'N/A'}</span>
                                            </div>
                                        </div>

                                        {hostel.facilities && (
                                            <div className="hostelrooms-hostel-info-section">
                                                <h3 className="hostelrooms-section-title">Facilities</h3>
                                                <p className="hostelrooms-facilities">
                                                    {hostel.facilities}
                                                </p>
                                            </div>
                                        )}

                                        {hostel.description && (
                                            <div className="hostelrooms-hostel-info-section">
                                                <h3 className="hostelrooms-section-title">Description</h3>
                                                <p className="hostelrooms-description">
                                                    {hostel.description}
                                                </p>
                                            </div>
                                        )}

                                        <div className="hostelrooms-hostel-stats">
                                            <div className="hostelrooms-hostel-stat-item">
                                                <span className="hostelrooms-stat-value">
                                                    {rooms.filter(room => room.hostelId === hostel._id).length}
                                                </span>
                                                <span className="hostelrooms-stat-label">Total Rooms</span>
                                            </div>
                                            <div className="hostelrooms-hostel-stat-item">
                                                <span className="hostelrooms-stat-value">
                                                    {rooms.filter(room => room.hostelId === hostel._id && room.status === 'available').length}
                                                </span>
                                                <span className="hostelrooms-stat-label">Available</span>
                                            </div>
                                            <div className="hostelrooms-hostel-stat-item">
                                                <span className="hostelrooms-stat-value">
                                                    {rooms.filter(room => room.hostelId === hostel._id && room.status === 'occupied').length}
                                                </span>
                                                <span className="hostelrooms-stat-label">Occupied</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="hostelrooms-hostel-footer">
                                        <button
                                            className="hostelrooms-action-btn hostelrooms-edit-btn"
                                            onClick={() => handleEditHostelClick(hostel)}
                                        >
                                            <FaEdit /> Edit
                                        </button>
                                        <button
                                            className="hostelrooms-action-btn hostelrooms-delete-btn"
                                            onClick={() => handleDeleteHostelClick(hostel)}
                                        >
                                            <FaTrash /> Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Rooms Tab Content */}
            {activeTab === 'rooms' && (
                <>
                    {filteredRooms.length === 0 && !isLoading ? (
                        <div className="hostelrooms-empty">
                            <div className="hostelrooms-empty-icon">
                                <FaBed size={48} color="#a0aec0" />
                            </div>
                            <p className="hostelrooms-empty-text">No rooms found matching your criteria.</p>
                            {searchTerm && (
                                <button className="hostelrooms-reset-search" onClick={() => setSearchTerm('')}>
                                    Clear Search
                                </button>
                            )}
                            {hostels.length > 0 && (
                                <button
                                    className="hostelrooms-add-btn"
                                    onClick={() => setShowAddRoomModal(true)}
                                >
                                    <FaPlus /> Add New Room
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="hostelrooms-rooms-grid">
                            {filteredRooms.map((room, index) => (
                                <div key={index} className={`hostelrooms-room-card ${room.status}`}>
                                    <div className="hostelrooms-room-header">
                                        <div className="hostelrooms-room-number">Room {room.roomNumber}</div>
                                        <div className="hostelrooms-room-status-badge">
                                            {room.status.charAt(0).toUpperCase() + room.status.slice(1)}
                                        </div>
                                    </div>

                                    <div className="hostelrooms-room-body">
                                        <div className="hostelrooms-hostel-name-tag">
                                            {getHostelNameById(room.hostelId)}
                                        </div>

                                        <div className="hostelrooms-room-info-section">
                                            <div className="hostelrooms-room-info-group">
                                                <span className="hostelrooms-room-info-label">Floor:</span>
                                                <span className="hostelrooms-room-info-value">{room.floor}</span>
                                            </div>
                                            <div className="hostelrooms-room-info-group">
                                                <span className="hostelrooms-room-info-label">Type:</span>
                                                <span className="hostelrooms-room-info-value">
                                                    {room.type.charAt(0).toUpperCase() + room.type.slice(1)}
                                                </span>
                                            </div>
                                            <div className="hostelrooms-room-info-group">
                                                <span className="hostelrooms-room-info-label">Capacity:</span>
                                                <span className="hostelrooms-room-info-value">{room.capacity} Students</span>
                                            </div>
                                            <div className="hostelrooms-room-info-group">
                                                <span className="hostelrooms-room-info-label">Occupants:</span>
                                                <span className="hostelrooms-room-info-value">{room.occupants}</span>
                                            </div>
                                            <div className="hostelrooms-room-info-group">
                                                <span className="hostelrooms-room-info-label">Rent:</span>
                                                <span className="hostelrooms-room-info-value">₹{room.rentPerMonth}/month</span>
                                            </div>
                                        </div>

                                        {room.amenities && (
                                            <div className="hostelrooms-room-info-section">
                                                <h3 className="hostelrooms-section-title">Amenities</h3>
                                                <p className="hostelrooms-amenities">
                                                    {room.amenities}
                                                </p>
                                            </div>
                                        )}

                                        <div className="hostelrooms-occupancy-meter">
                                            <div className="hostelrooms-meter-label">Occupancy</div>
                                            <div className="hostelrooms-meter-bar">
                                                <div
                                                    className="hostelrooms-meter-fill"
                                                    style={{ width: `${(room.occupants / room.capacity) * 100}%` }}
                                                ></div>
                                            </div>
                                            <div className="hostelrooms-meter-stats">
                                                <span>{room.occupants}/{room.capacity}</span>
                                                <span>{Math.round((room.occupants / room.capacity) * 100)}%</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="hostelrooms-room-footer">
                                        <button
                                            className="hostelrooms-action-btn hostelrooms-edit-btn"
                                            onClick={() => handleEditRoomClick(room)}
                                        >
                                            <FaEdit /> Edit
                                        </button>
                                        <button
                                            className="hostelrooms-action-btn hostelrooms-delete-btn"
                                            onClick={() => handleDeleteRoomClick(room)}
                                        >
                                            <FaTrash /> Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Add Hostel Modal */}
            {showAddHostelModal && (
                <div className="hostelrooms-modal-overlay">
                    <div className="hostelrooms-modal">
                        <div className="hostelrooms-modal-header">
                            <h3>Add New Hostel</h3>
                            <button className="hostelrooms-modal-close" onClick={() => setShowAddHostelModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleAddHostelSubmit} className="hostelrooms-form">
                            <div className="hostelrooms-form-group">
                                <label htmlFor="name">Hostel Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={hostelFormData.name}
                                    onChange={handleHostelInputChange}
                                    required
                                    placeholder="Enter hostel name"
                                />
                            </div>
                            <div className="hostelrooms-form-row">
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="type">Hostel Type *</label>
                                    <select
                                        id="type"
                                        name="type"
                                        value={hostelFormData.type}
                                        onChange={handleHostelInputChange}
                                        required
                                    >
                                        <option value="boys">Boys</option>
                                        <option value="girls">Girls</option>
                                        <option value="mixed">Mixed</option>
                                    </select>
                                </div>
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="capacity">Capacity *</label>
                                    <input
                                        type="number"
                                        id="capacity"
                                        name="capacity"
                                        value={hostelFormData.capacity}
                                        onChange={handleHostelInputChange}
                                        required
                                        placeholder="Total capacity"
                                        min="1"
                                    />
                                </div>
                            </div>
                            <div className="hostelrooms-form-group">
                                <label htmlFor="location">Location</label>
                                <input
                                    type="text"
                                    id="location"
                                    name="location"
                                    value={hostelFormData.location}
                                    onChange={handleHostelInputChange}
                                    placeholder="Enter hostel location"
                                />
                            </div>
                            <div className="hostelrooms-form-row">
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="warden">Warden Name</label>
                                    <input
                                        type="text"
                                        id="warden"
                                        name="warden"
                                        value={hostelFormData.warden}
                                        onChange={handleHostelInputChange}
                                        placeholder="Enter warden name"
                                    />
                                </div>
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="contactNumber">Contact Number</label>
                                    <input
                                        type="text"
                                        id="contactNumber"
                                        name="contactNumber"
                                        value={hostelFormData.contactNumber}
                                        onChange={handleHostelInputChange}
                                        placeholder="Enter contact number"
                                    />
                                </div>
                            </div>
                            <div className="hostelrooms-form-group">
                                <label htmlFor="facilities">Facilities</label>
                                <textarea
                                    id="facilities"
                                    name="facilities"
                                    value={hostelFormData.facilities}
                                    onChange={handleHostelInputChange}
                                    placeholder="List available facilities (e.g., WiFi, Laundry, Mess, etc.)"
                                    rows="3"
                                ></textarea>
                            </div>
                            <div className="hostelrooms-form-group">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={hostelFormData.description}
                                    onChange={handleHostelInputChange}
                                    placeholder="Enter a brief description of the hostel"
                                    rows="3"
                                ></textarea>
                            </div>
                            <div className="hostelrooms-form-actions">
                                <button type="button" className="hostelrooms-cancel-btn" onClick={() => setShowAddHostelModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="hostelrooms-submit-btn">
                                    Add Hostel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Hostel Modal */}
            {showEditHostelModal && (
                <div className="hostelrooms-modal-overlay">
                    <div className="hostelrooms-modal">
                        <div className="hostelrooms-modal-header">
                            <h3>Edit Hostel</h3>
                            <button className="hostelrooms-modal-close" onClick={() => setShowEditHostelModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateHostelSubmit} className="hostelrooms-form">
                            <div className="hostelrooms-form-group">
                                <label htmlFor="edit-name">Hostel Name *</label>
                                <input
                                    type="text"
                                    id="edit-name"
                                    name="name"
                                    value={hostelFormData.name}
                                    onChange={handleHostelInputChange}
                                    required
                                    placeholder="Enter hostel name"
                                />
                            </div>
                            <div className="hostelrooms-form-row">
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="edit-type">Hostel Type *</label>
                                    <select
                                        id="edit-type"
                                        name="type"
                                        value={hostelFormData.type}
                                        onChange={handleHostelInputChange}
                                        required
                                    >
                                        <option value="boys">Boys</option>
                                        <option value="girls">Girls</option>
                                        <option value="mixed">Mixed</option>
                                    </select>
                                </div>
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="edit-capacity">Capacity *</label>
                                    <input
                                        type="number"
                                        id="edit-capacity"
                                        name="capacity"
                                        value={hostelFormData.capacity}
                                        onChange={handleHostelInputChange}
                                        required
                                        placeholder="Total capacity"
                                        min="1"
                                    />
                                </div>
                            </div>
                            <div className="hostelrooms-form-group">
                                <label htmlFor="edit-location">Location</label>
                                <input
                                    type="text"
                                    id="edit-location"
                                    name="location"
                                    value={hostelFormData.location}
                                    onChange={handleHostelInputChange}
                                    placeholder="Enter hostel location"
                                />
                            </div>
                            <div className="hostelrooms-form-row">
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="edit-warden">Warden Name</label>
                                    <input
                                        type="text"
                                        id="edit-warden"
                                        name="warden"
                                        value={hostelFormData.warden}
                                        onChange={handleHostelInputChange}
                                        placeholder="Enter warden name"
                                    />
                                </div>
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="edit-contactNumber">Contact Number</label>
                                    <input
                                        type="text"
                                        id="edit-contactNumber"
                                        name="contactNumber"
                                        value={hostelFormData.contactNumber}
                                        onChange={handleHostelInputChange}
                                        placeholder="Enter contact number"
                                    />
                                </div>
                            </div>
                            <div className="hostelrooms-form-group">
                                <label htmlFor="edit-facilities">Facilities</label>
                                <textarea
                                    id="edit-facilities"
                                    name="facilities"
                                    value={hostelFormData.facilities}
                                    onChange={handleHostelInputChange}
                                    placeholder="List available facilities (e.g., WiFi, Laundry, Mess, etc.)"
                                    rows="3"
                                ></textarea>
                            </div>
                            <div className="hostelrooms-form-group">
                                <label htmlFor="edit-description">Description</label>
                                <textarea
                                    id="edit-description"
                                    name="description"
                                    value={hostelFormData.description}
                                    onChange={handleHostelInputChange}
                                    placeholder="Enter a brief description of the hostel"
                                    rows="3"
                                ></textarea>
                            </div>
                            <div className="hostelrooms-form-actions">
                                <button type="button" className="hostelrooms-cancel-btn" onClick={() => setShowEditHostelModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="hostelrooms-submit-btn">
                                    Update Hostel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Hostel Modal */}
            {showDeleteHostelModal && (
                <div className="hostelrooms-modal-overlay">
                    <div className="hostelrooms-modal hostelrooms-delete-modal">
                        <div className="hostelrooms-modal-header">
                            <h3>Delete Hostel</h3>
                            <button className="hostelrooms-modal-close" onClick={() => setShowDeleteHostelModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="hostelrooms-delete-content">
                            <div className="hostelrooms-delete-warning">
                                <FaExclamationTriangle className="hostelrooms-warning-icon" />
                                <p>Are you sure you want to delete <strong>{currentHostel?.name}</strong>?</p>
                                <p className="hostelrooms-delete-note">This action cannot be undone. All associated rooms will also be deleted.</p>
                            </div>
                            <div className="hostelrooms-form-actions">
                                <button type="button" className="hostelrooms-cancel-btn" onClick={() => setShowDeleteHostelModal(false)}>
                                    Cancel
                                </button>
                                <button type="button" className="hostelrooms-delete-confirm-btn" onClick={handleDeleteHostelConfirm}>
                                    Delete Hostel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Room Modal */}
            {showAddRoomModal && (
                <div className="hostelrooms-modal-overlay">
                    <div className="hostelrooms-modal">
                        <div className="hostelrooms-modal-header">
                            <h3>Add New Room</h3>
                            <button className="hostelrooms-modal-close" onClick={() => setShowAddRoomModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleAddRoomSubmit} className="hostelrooms-form">
                            <div className="hostelrooms-form-row">
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="roomNumber">Room Number *</label>
                                    <input
                                        type="text"
                                        id="roomNumber"
                                        name="roomNumber"
                                        value={roomFormData.roomNumber}
                                        onChange={handleRoomInputChange}
                                        required
                                        placeholder="Enter room number"
                                    />
                                </div>
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="hostelId">Hostel *</label>
                                    <select
                                        id="hostelId"
                                        name="hostelId"
                                        value={roomFormData.hostelId}
                                        onChange={handleRoomInputChange}
                                        required
                                    >
                                        <option value="">Select Hostel</option>
                                        {hostels.map(hostel => (
                                            <option key={hostel._id} value={hostel._id}>{hostel.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="hostelrooms-form-row">
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="capacity">Capacity *</label>
                                    <input
                                        type="number"
                                        id="capacity"
                                        name="capacity"
                                        value={roomFormData.capacity}
                                        onChange={handleRoomInputChange}
                                        required
                                        placeholder="Max occupants"
                                        min="1"
                                    />
                                </div>
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="floor">Floor *</label>
                                    <input
                                        type="number"
                                        id="floor"
                                        name="floor"
                                        value={roomFormData.floor}
                                        onChange={handleRoomInputChange}
                                        required
                                        placeholder="Floor number"
                                    />
                                </div>
                            </div>
                            <div className="hostelrooms-form-row">
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="type">Room Type *</label>
                                    <select
                                        id="type"
                                        name="type"
                                        value={roomFormData.type}
                                        onChange={handleRoomInputChange}
                                        required
                                    >
                                        <option value="standard">Standard</option>
                                        <option value="deluxe">Deluxe</option>
                                        <option value="premium">Premium</option>
                                        <option value="special">Special</option>
                                    </select>
                                </div>
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="status">Status *</label>
                                    <select
                                        id="status"
                                        name="status"
                                        value={roomFormData.status}
                                        onChange={handleRoomInputChange}
                                        required
                                    >
                                        <option value="available">Available</option>
                                        <option value="occupied">Occupied</option>
                                        <option value="maintenance">Under Maintenance</option>
                                    </select>
                                </div>
                            </div>
                            <div className="hostelrooms-form-row">
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="occupants">Current Occupants</label>
                                    <input
                                        type="number"
                                        id="occupants"
                                        name="occupants"
                                        value={roomFormData.occupants}
                                        onChange={handleRoomInputChange}
                                        placeholder="Current occupants"
                                        min="0"
                                    />
                                </div>
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="rentPerMonth">Rent per Month (₹) *</label>
                                    <input
                                        type="number"
                                        id="rentPerMonth"
                                        name="rentPerMonth"
                                        value={roomFormData.rentPerMonth}
                                        onChange={handleRoomInputChange}
                                        required
                                        placeholder="Monthly rent"
                                        min="0"
                                    />
                                </div>
                            </div>
                            <div className="hostelrooms-form-group">
                                <label htmlFor="amenities">Amenities</label>
                                <textarea
                                    id="amenities"
                                    name="amenities"
                                    value={roomFormData.amenities}
                                    onChange={handleRoomInputChange}
                                    placeholder="List available amenities (e.g., Air Conditioning, TV, Attached Bathroom, etc.)"
                                    rows="3"
                                ></textarea>
                            </div>
                            <div className="hostelrooms-form-actions">
                                <button type="button" className="hostelrooms-cancel-btn" onClick={() => setShowAddRoomModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="hostelrooms-submit-btn">
                                    Add Room
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Room Modal */}
            {showEditRoomModal && (
                <div className="hostelrooms-modal-overlay">
                    <div className="hostelrooms-modal">
                        <div className="hostelrooms-modal-header">
                            <h3>Edit Room</h3>
                            <button className="hostelrooms-modal-close" onClick={() => setShowEditRoomModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateRoomSubmit} className="hostelrooms-form">
                            <div className="hostelrooms-form-row">
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="edit-roomNumber">Room Number *</label>
                                    <input
                                        type="text"
                                        id="edit-roomNumber"
                                        name="roomNumber"
                                        value={roomFormData.roomNumber}
                                        onChange={handleRoomInputChange}
                                        required
                                        placeholder="Enter room number"
                                    />
                                </div>
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="edit-hostelId">Hostel *</label>
                                    <select
                                        id="edit-hostelId"
                                        name="hostelId"
                                        value={roomFormData.hostelId}
                                        onChange={handleRoomInputChange}
                                        required
                                    >
                                        <option value="">Select Hostel</option>
                                        {hostels.map(hostel => (
                                            <option key={hostel._id} value={hostel._id}>{hostel.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="hostelrooms-form-row">
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="edit-capacity">Capacity *</label>
                                    <input
                                        type="number"
                                        id="edit-capacity"
                                        name="capacity"
                                        value={roomFormData.capacity}
                                        onChange={handleRoomInputChange}
                                        required
                                        placeholder="Max occupants"
                                        min="1"
                                    />
                                </div>
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="edit-floor">Floor *</label>
                                    <input
                                        type="number"
                                        id="edit-floor"
                                        name="floor"
                                        value={roomFormData.floor}
                                        onChange={handleRoomInputChange}
                                        required
                                        placeholder="Floor number"
                                    />
                                </div>
                            </div>
                            <div className="hostelrooms-form-row">
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="edit-type">Room Type *</label>
                                    <select
                                        id="edit-type"
                                        name="type"
                                        value={roomFormData.type}
                                        onChange={handleRoomInputChange}
                                        required
                                    >
                                        <option value="standard">Standard</option>
                                        <option value="deluxe">Deluxe</option>
                                        <option value="premium">Premium</option>
                                        <option value="special">Special</option>
                                    </select>
                                </div>
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="edit-status">Status *</label>
                                    <select
                                        id="edit-status"
                                        name="status"
                                        value={roomFormData.status}
                                        onChange={handleRoomInputChange}
                                        required
                                    >
                                        <option value="available">Available</option>
                                        <option value="occupied">Occupied</option>
                                        <option value="maintenance">Under Maintenance</option>
                                    </select>
                                </div>
                            </div>
                            <div className="hostelrooms-form-row">
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="edit-occupants">Current Occupants</label>
                                    <input
                                        type="number"
                                        id="edit-occupants"
                                        name="occupants"
                                        value={roomFormData.occupants}
                                        onChange={handleRoomInputChange}
                                        placeholder="Current occupants"
                                        min="0"
                                    />
                                </div>
                                <div className="hostelrooms-form-group">
                                    <label htmlFor="edit-rentPerMonth">Rent per Month (₹) *</label>
                                    <input
                                        type="number"
                                        id="edit-rentPerMonth"
                                        name="rentPerMonth"
                                        value={roomFormData.rentPerMonth}
                                        onChange={handleRoomInputChange}
                                        required
                                        placeholder="Monthly rent"
                                        min="0"
                                    />
                                </div>
                            </div>
                            <div className="hostelrooms-form-group">
                                <label htmlFor="edit-amenities">Amenities</label>
                                <textarea
                                    id="edit-amenities"
                                    name="amenities"
                                    value={roomFormData.amenities}
                                    onChange={handleRoomInputChange}
                                    placeholder="List available amenities (e.g., Air Conditioning, TV, Attached Bathroom, etc.)"
                                    rows="3"
                                ></textarea>
                            </div>
                            <div className="hostelrooms-form-actions">
                                <button type="button" className="hostelrooms-cancel-btn" onClick={() => setShowEditRoomModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="hostelrooms-submit-btn">
                                    Update Room
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Room Modal */}
            {showDeleteRoomModal && (
                <div className="hostelrooms-modal-overlay">
                    <div className="hostelrooms-modal hostelrooms-delete-modal">
                        <div className="hostelrooms-modal-header">
                            <h3>Delete Room</h3>
                            <button className="hostelrooms-modal-close" onClick={() => setShowDeleteRoomModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="hostelrooms-delete-content">
                            <div className="hostelrooms-delete-warning">
                                <FaExclamationTriangle className="hostelrooms-warning-icon" />
                                <p>Are you sure you want to delete <strong>Room {currentRoom?.roomNumber}</strong> from <strong>{getHostelNameById(currentRoom?.hostelId)}</strong>?</p>
                                <p className="hostelrooms-delete-note">This action cannot be undone.</p>
                            </div>
                            <div className="hostelrooms-form-actions">
                                <button type="button" className="hostelrooms-cancel-btn" onClick={() => setShowDeleteRoomModal(false)}>
                                    Cancel
                                </button>
                                <button type="button" className="hostelrooms-delete-confirm-btn" onClick={handleDeleteRoomConfirm}>
                                    Delete Room
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default HostelRooms;